import {
  CircleDot,
  Coffee,
  FileText,
  Pause,
  Power,
  Sparkles,
} from 'lucide-react';
import styles from './ControlEstadoMedico.module.css';

export type EstadoMedico =
  | 'disponible'
  | 'pausa'
  | 'offline'
  | 'en_consulta'
  | 'charting'         // M8: post-consulta, completando notas
  | 'break_almuerzo';  // M8: break programado 30-60 min

export interface ControlEstadoMedicoProps {
  estado: EstadoMedico;
  autoAsignacionEnabled: boolean;
  onChangeEstado: (estado: EstadoMedico) => void | Promise<void>;
  onToggleAutoAsignacion: (next: boolean) => void | Promise<void>;
  /** M8: callback opcional cuando el médico marca "terminé notas" */
  onTerminarCharting?: () => void | Promise<void>;
  /** M8: callback opcional para iniciar/terminar break de almuerzo */
  onIniciarBreakAlmuerzo?: () => void | Promise<void>;
  onTerminarBreakAlmuerzo?: () => void | Promise<void>;
  /** Disable controls while a server call is in-flight */
  loading?: boolean;
}

/**
 * Barra de turno del médico: disponibilidad, almuerzo y auto-asignación.
 *
 * Es horizontal a propósito. Vivía en un riel de 280 px a la derecha de la
 * cola, y ese riel le quitaba a la tabla justo el ancho que necesitaban las
 * columnas de paciente y motivo. Como turno son tres controles y ningún dato
 * que crezca, cabe de sobra en una franja sobre la cola.
 */
export function ControlEstadoMedico({
  estado,
  autoAsignacionEnabled,
  onChangeEstado,
  onToggleAutoAsignacion,
  onTerminarCharting,
  onIniciarBreakAlmuerzo,
  onTerminarBreakAlmuerzo,
  loading = false,
}: ControlEstadoMedicoProps) {
  const isDisponible = estado === 'disponible';
  const isPausa = estado === 'pausa';
  const isEnConsulta = estado === 'en_consulta';
  const isCharting = estado === 'charting';
  const isBreak = estado === 'break_almuerzo';

  const handleStateClick = () => {
    if (isEnConsulta || isCharting || isBreak) return; // usar el botón dedicado para salir de estos estados
    onChangeEstado(isDisponible ? 'pausa' : 'disponible');
  };

  const estadoLabel = isEnConsulta
    ? 'En consulta'
    : isCharting
      ? 'Completando notas'
      : isBreak
        ? 'En almuerzo'
        : isDisponible
          ? 'Disponible'
          : isPausa
            ? 'En pausa'
            : 'Offline';

  // Explica qué significa el estado actual y cómo cambiarlo — el botón
  // Disponible/Pausa por sí solo no deja claro que alterna entre ambos.
  const estadoHint = isEnConsulta
    ? 'Está atendiendo una consulta. El estado cambiará automáticamente al finalizar.'
    : isCharting
      ? 'Completando notas de la consulta anterior. Pasará a "Disponible" al terminar el buffer.'
      : isBreak
        ? 'En break de almuerzo — no recibe pacientes.'
        : isDisponible
          ? 'Recibiendo pacientes nuevos en sus salas.'
          : 'No está recibiendo pacientes.';

  return (
    <div className={styles.barra} role="group" aria-label="Control de turno">
      {/* Disponibilidad */}
      <div className={styles.grupoEstado}>
        <button
          type="button"
          className={`${styles.stateBtn} ${
            isEnConsulta
              ? styles.stateInCall
              : isDisponible
                ? styles.stateAvailable
                : styles.statePaused
          }`}
          onClick={handleStateClick}
          disabled={loading || isEnConsulta || isCharting || isBreak}
          title={
            isEnConsulta
              ? 'Está en una consulta activa'
              : isDisponible
                ? 'Pausar disponibilidad'
                : 'Volver a disponible'
          }
          aria-pressed={isDisponible}
        >
          <span className={styles.stateDot} aria-hidden="true" />
          <span className={styles.stateLabel}>{estadoLabel}</span>
          {!isEnConsulta && !isCharting && !isBreak && (
            <span className={styles.stateAction}>
              {isDisponible ? (
                <>
                  <Pause size={12} aria-hidden="true" /> Pausar
                </>
              ) : (
                <>
                  <Power size={12} aria-hidden="true" /> Activar
                </>
              )}
            </span>
          )}
        </button>
        <p className={styles.stateHint}>{estadoHint}</p>
      </div>

      {/* M8: acciones contextuales charting / break */}
      <div className={styles.acciones}>
        {isCharting && onTerminarCharting && (
          <button
            type="button"
            className={styles.btnSecundario}
            onClick={() => onTerminarCharting()}
            disabled={loading}
            title="Marcar notas como completas"
          >
            <FileText size={13} aria-hidden="true" />
            <span>Terminar notas</span>
          </button>
        )}

        {!isEnConsulta && !isCharting && (
          isBreak ? (
            <button
              type="button"
              className={`${styles.btnSecundario} ${styles.btnRegresar}`}
              onClick={() => onTerminarBreakAlmuerzo?.()}
              disabled={loading || !onTerminarBreakAlmuerzo}
              title="Regresar de almuerzo"
            >
              <Coffee size={13} aria-hidden="true" />
              <span>Regresar de almuerzo</span>
            </button>
          ) : (
            <button
              type="button"
              className={styles.btnSecundario}
              onClick={() => onIniciarBreakAlmuerzo?.()}
              disabled={loading || !onIniciarBreakAlmuerzo || !isDisponible}
              title="Iniciar break programado de almuerzo"
            >
              <Coffee size={13} aria-hidden="true" />
              <span>Almuerzo</span>
            </button>
          )
        )}
      </div>

      {/* Auto-asignación */}
      <div className={styles.autoRow}>
        <button
          type="button"
          className={`${styles.toggleSwitch} ${autoAsignacionEnabled ? styles.toggleOn : ''}`}
          onClick={() => onToggleAutoAsignacion(!autoAsignacionEnabled)}
          disabled={loading}
          role="switch"
          aria-checked={autoAsignacionEnabled}
          aria-label="Activar auto-asignación"
        >
          <span className={styles.toggleKnob} aria-hidden="true" />
        </button>
        <div className={styles.autoInfo}>
          <span className={styles.autoLabel}>
            <Sparkles size={12} aria-hidden="true" /> Auto-asignación
          </span>
          <span className={styles.autoHint}>
            {autoAsignacionEnabled
              ? 'El sistema le asigna pacientes'
              : 'Usted los toma de la cola'}
          </span>
        </div>
      </div>

      {/* Modo resultante (informativo) */}
      <div className={styles.modeChip} title="Cómo le llegan los pacientes con la configuración actual">
        <CircleDot size={11} aria-hidden="true" />
        <span>
          Modo <strong>{autoAsignacionEnabled ? 'Híbrido' : 'Manual'}</strong>
        </span>
      </div>
    </div>
  );
}
